/**
 * This hook provides a list of assessment screening windows plus the list of all periods in the assessment calendar.
 * It uses the useCalendar hook to fetch the assessment calendar data to create the period portions of the list.
 * It uses the assignment service to fetch the current district assignment object and create the screening window portion of the list.
 * Combine them and return the list of assessment screening windows.
 */
import { useCalendar } from "./useCalendar";
import { useRosterInfo } from "./useRosterInfo";
import { Assignment } from "@/services/assignment/assignmentTypesAPI";
import { getMostCommonGrade } from "@/utils/gradeUtils";

export enum AssessmentPeriodWindowType {
  DISTRICT_ASSIGNMENT = 'DistrictAssignment',
  SCHOOL_ASSIGNMENT = 'SchoolAssignment',
  PERIOD = 'Period'
}

export enum AssessmentWindowScreeningWindows {
  BOY = 'BOY',
  MOY = 'MOY',
  EOY = 'EOY'
}

export interface AssessmentPeriodItem {
  assignment: Assignment | null;
  assignmentType: string;
  periodId: number;
  current: boolean;
  startDate: string;
  endDate: string;
  tags: string[];
  screeningWindowType: AssessmentPeriodWindowType;
  window?: AssessmentWindowScreeningWindows | null;
  assignmentId: string | null;
}

export type AssessmentPeriodWindow = {
  loading: boolean;
  assessmentPeriodWindows: AssessmentPeriodItem[];
}

export const useAssessmentPeriodWindows = (): AssessmentPeriodWindow => {
  const { assessmentCalendar, assessmentDistrictWindows, loading, schoolYearCalendar } = useCalendar();
  const rosterData = useRosterInfo()

  // Group assignments by grade and tag for prioritization
  const assignmentsByGrade = assessmentDistrictWindows?.assignment.reduce((acc: Record<string, any[]>, assignment: any) => {
    const grade = assignment.grade;
    if (!acc[grade]) {
      acc[grade] = [];
    }
    acc[grade].push(assignment);
    return acc;
  }, {});

  let mostCommonGrade = '';
  if (rosterData && rosterData.students) {
    mostCommonGrade = getMostCommonGrade(rosterData.students);
  }

  if (!assessmentCalendar && loading) {
    return {
      loading,
      assessmentPeriodWindows: [],
    };
  }
  if (!assessmentCalendar) {
    return {
      loading,
      assessmentPeriodWindows: [],
    };
  }
  if (!assessmentDistrictWindows) {
    return {
      loading,
      assessmentPeriodWindows: [],
    };
  }

  const today = new Date();

  const periodWindows = (assessmentCalendar.periods ?? []).map((period) => {
    const periodStart = new Date(period.startDate);
    const periodEnd = new Date(period.endDate);
    const isPeriodItselfCurrent = today >= periodStart && today <= periodEnd;

    return {
      assignment: null,
      assignmentType: period.name,
      periodId: period.periodIdentifier,
      current: isPeriodItselfCurrent,
      startDate: period.startDate,
      endDate: period.endDate,
      tags: period.tags,
      screeningWindowType: AssessmentPeriodWindowType.PERIOD,
      assignmentId: null
    };
  });

  // Create prioritized assignment windows
  const createPrioritizedAssignmentWindows = () => {
    const gradeAssignments = assignmentsByGrade?.[mostCommonGrade] || [];
    const districtGradeAssignments = gradeAssignments.filter(a => a.entityType === 'DISTRICT');
    const schoolGradeAssignments = gradeAssignments.filter(a => a.entityType === 'SCHOOL');

    // Priority logic: for teacher-scheduled assessments, use district assignment periodId if available, otherwise use school assignment periodId
    const prioritizedAssignments: Array<{
      assignment: Assignment;
      screeningWindowType: AssessmentPeriodWindowType;
      periodId: number;
      isFromDistrict: boolean;
    }> = [];
    const usedCombinations = new Set<string>();

    // First pass: Add district assignments (deduplicate within district assignments)
    districtGradeAssignments.forEach(assignment => {
      // For progress monitoring, include periodId in the key to allow multiple windows
      const key = assignment.assignmentType === 'PROGRESS_MONITORING' 
        ? `${assignment.assignmentType}-${assignment.periodId}-${Array.isArray(assignment.tags) ? assignment.tags.join(',') : assignment.tags || ''}`
        : `${assignment.assignmentType}-${Array.isArray(assignment.tags) ? assignment.tags.join(',') : assignment.tags || ''}`;
      if (!usedCombinations.has(key)) {
        usedCombinations.add(key);
        prioritizedAssignments.push({
          assignment,
          screeningWindowType: AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT,
          periodId: Number(assignment.periodId),
          isFromDistrict: true
        });
      }
    });

    // Second pass: Add school assignments only if no district assignment exists for the same type/tag combination
    schoolGradeAssignments.forEach(assignment => {
      // For progress monitoring, include periodId in the key to allow multiple windows
      const key = assignment.assignmentType === 'PROGRESS_MONITORING' 
        ? `${assignment.assignmentType}-${assignment.periodId}-${Array.isArray(assignment.tags) ? assignment.tags.join(',') : assignment.tags || ''}`
        : `${assignment.assignmentType}-${Array.isArray(assignment.tags) ? assignment.tags.join(',') : assignment.tags || ''}`;
      if (!usedCombinations.has(key)) {
        prioritizedAssignments.push({
          assignment,
          screeningWindowType: AssessmentPeriodWindowType.SCHOOL_ASSIGNMENT,
          periodId: Number(assignment.periodId),
          isFromDistrict: false
        });
      }
    });

    return prioritizedAssignments.sort((a, b) => Number(a.assignment.periodId) - Number(b.assignment.periodId));
  };

  const prioritizedAssignmentWindows = createPrioritizedAssignmentWindows();

  const assignmentWindows = prioritizedAssignmentWindows.map((item) => {
    const { assignment: a, screeningWindowType, periodId } = item;
    const start = new Date(a.dateFrom!);
    const end = new Date(a.dateTo!);
    const isCurrent = today >= start && today <= end;

    return {
      assignment: a,
      assignmentType: a.assignmentType || '',
      periodId: periodId,
      current: isCurrent,
      startDate: a.dateFrom!.split(' ')[0],
      endDate: a.dateTo!.split(' ')[0],
      tags: a.tags || [],
      screeningWindowType: screeningWindowType,
      assignmentId: a.assignmentId
    };
  });

  const currentAssignment = assignmentWindows.find(a => a.current === true);
  const currentPeriod = periodWindows.find(p => p.current === true);
  const currentWindow = currentAssignment || currentPeriod;

  const remainingAssignments = (currentWindow === currentAssignment)
    ? assignmentWindows.filter(a => a !== currentAssignment)
    : assignmentWindows;

  const remainingPeriods = (currentWindow === currentPeriod)
    ? periodWindows.filter(p => p !== currentPeriod)
    : periodWindows;

  const orderedWindows = [
    ...(currentWindow ? [currentWindow] : []),
    ...remainingAssignments,
    ...remainingPeriods
  ];

  return {
    loading,
    assessmentPeriodWindows: orderedWindows as AssessmentPeriodItem[],
  };
};
